
import React, { useState, useEffect } from 'react';
import { Inpatient, HospitalLocation } from '../types';
import { dbService } from '../lib/firebase';
import { BedDouble, Plus, Edit2, Trash2, X, Save, Search, Stethoscope, User, Printer, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortablePatientRowProps {
    patient: Inpatient;
    isSearchActive: boolean;
    onEdit: (patient: Inpatient) => void;
    onDelete: (patient: Inpatient) => void;
}

const SortablePatientRow: React.FC<SortablePatientRowProps> = ({ patient, isSearchActive, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: patient.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : 0
    };

    return (
        <tr ref={isSearchActive ? undefined : setNodeRef} style={isSearchActive ? {} : style} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group">
            <td className="p-2 w-10 text-center">
                {!isSearchActive && (
                    <button 
                        className="p-1.5 text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400 cursor-grab active:cursor-grabbing"
                        {...attributes}
                        {...listeners}
                    >
                        <GripVertical className="w-4 h-4" />
                    </button>
                )}
            </td>
            <td className="p-4 font-bold text-slate-800 dark:text-slate-200">{patient.name}</td>
            <td className="p-4 text-slate-600 dark:text-slate-400">{patient.signalment}</td>
            <td className="p-4 text-slate-600 dark:text-slate-400">{patient.diagnosis}</td>
            <td className="p-4 text-slate-600 dark:text-slate-400">{patient.procedure}</td>
            <td className="p-4">
                {patient.pointOfContact && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 uppercase whitespace-nowrap">
                        <User className="w-3 h-3" /> {patient.pointOfContact}
                    </span>
                )}
            </td>
            <td className="p-4 text-center">
                <input 
                    type="checkbox" 
                    checked={patient.trazodone || false} 
                    onChange={(e) => dbService.saveInpatient({...patient, trazodone: e.target.checked})}
                    className="w-4 h-4 text-indigo-600 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:ring-indigo-500 cursor-pointer"
                />
            </td>
            <td className="p-4 text-center">
                <input 
                    type="checkbox" 
                    checked={patient.replaceIvc || false} 
                    onChange={(e) => dbService.saveInpatient({...patient, replaceIvc: e.target.checked})}
                    className="w-4 h-4 text-indigo-600 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:ring-indigo-500 cursor-pointer"
                />
            </td>
            <td className="p-4 text-slate-500 dark:text-slate-400 text-xs italic">{patient.notes}</td>
            <td className="p-4 text-right">
                <div className="flex justify-end gap-1">
                    <button onClick={() => onEdit(patient)} className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors" title="Edit"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(patient); }} className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
            </td>
        </tr>
    );
};

export const InpatientManager: React.FC = () => {
    const [inpatients, setInpatients] = useState<Inpatient[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<Partial<Inpatient>>({});
    const [collapsedLocations, setCollapsedLocations] = useState<Record<string, boolean>>({});

    const toggleCollapse = (location: string) => {
        setCollapsedLocations(prev => ({
            ...prev,
            [location]: !prev[location]
        }));
    };

    // Delete Confirmation State
    const [patientToDelete, setPatientToDelete] = useState<Inpatient | null>(null);

    useEffect(() => {
        const unsubscribe = dbService.subscribeToInpatients((data) => {
            setInpatients(data);
        });
        return () => unsubscribe();
    }, []);

    const handleAdd = (location: HospitalLocation) => {
        setForm({
            location,
            name: '',
            signalment: '',
            diagnosis: '',
            procedure: '',
            pointOfContact: '',
            notes: ''
        });
        setEditingId(null);
        setIsModalOpen(true);
    };

    const handleEdit = (patient: Inpatient) => {
        setForm({ ...patient });
        setEditingId(patient.id);
        setIsModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!patientToDelete) return;
        await dbService.deleteInpatient(patientToDelete.id);
        setPatientToDelete(null);
    };

    const handlePrint = (location: HospitalLocation, listToPrint: Inpatient[]) => {
        // Open with specific dimensions to encourage a new window/pop-up behavior which is often better for printing tasks
        const printWindow = window.open('', '_blank', 'width=1000,height=800,resizable=yes,scrollbars=yes');
        
        if (!printWindow) {
            alert("Please allow popups to print the list. Check your browser settings.");
            return;
        }

        const date = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${location} Rounds List - ${date}</title>
                <style>
                    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 20px; max-width: 1000px; margin: 0 auto; color: #000; background: #fff; }
                    .header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
                    h1 { margin: 0; font-size: 24px; color: #000; }
                    .date { font-size: 14px; color: #444; }
                    table { width: 100%; border-collapse: collapse; }
                    th { text-align: left; font-size: 12px; text-transform: uppercase; color: #444; border-bottom: 2px solid #ccc; padding: 8px 4px; font-weight: bold; }
                    td { padding: 12px 4px; vertical-align: top; font-size: 14px; color: #000; }
                    .row-main td { border-bottom: none; padding-bottom: 4px; }
                    .row-notes td { border-bottom: 1px solid #aaa; padding-top: 4px; padding-bottom: 16px; }
                    .patient-name { font-weight: bold; font-size: 15px; }
                    .notes-label { font-weight: bold; font-size: 12px; color: #555; text-transform: uppercase; margin-right: 8px; }
                    .notes { color: #222; font-style: italic; white-space: pre-wrap; font-size: 13px; }
                    .empty { text-align: center; padding: 40px; color: #666; font-style: italic; }
                    @media print {
                        body { padding: 0; max-width: none; -webkit-print-color-adjust: exact; }
                        button { display: none; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>${location} Inpatient List</h1>
                    <div class="date">${date}</div>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th width="15%">Patient</th>
                            <th width="15%">Signalment</th>
                            <th width="20%">Diagnosis</th>
                            <th width="20%">Procedure</th>
                            <th width="10%">POC</th>
                            <th width="10%">Trazodone</th>
                            <th width="10%">Replace IVC</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${listToPrint.length === 0 ? 
                            '<tr><td colspan="7" class="empty">No active inpatients listed.</td></tr>' : 
                            listToPrint.map(p => `
                            <tr class="row-main">
                                <td class="patient-name">${p.name || ''}</td>
                                <td>${p.signalment || ''}</td>
                                <td>${p.diagnosis || ''}</td>
                                <td>${p.procedure || ''}</td>
                                <td>${p.pointOfContact || ''}</td>
                                <td>${p.trazodone ? 'Yes' : 'No'}</td>
                                <td>${p.replaceIvc ? 'Yes' : 'No'}</td>
                            </tr>
                            <tr class="row-notes">
                                <td colspan="7">
                                    ${p.notes ? `
                                        <span class="notes-label">Notes:</span>
                                        <span class="notes">${p.notes}</span>
                                    ` : `
                                        <span class="notes-label">Notes:</span>
                                        <span class="notes" style="color: #999;">None</span>
                                    `}
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;

        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();

        // Use a small timeout to ensure styles are loaded and DOM is ready before printing
        // This is more reliable than window.onload for dynamically written content in some browsers
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
        }, 500);
    };

    const handleSave = async () => {
        if (!form.name || !form.location) return;

        const isNew = !editingId;
        const locationPatients = inpatients.filter(p => p.location === form.location);
        const nextOrder = locationPatients.length > 0 
            ? Math.max(...locationPatients.map(p => p.order ?? 0)) + 1 
            : 0;

        const newPatient: Inpatient = {
            id: editingId || `pt-${Date.now()}`,
            location: form.location!,
            name: form.name,
            signalment: form.signalment || '',
            diagnosis: form.diagnosis || '',
            procedure: form.procedure || '',
            pointOfContact: form.pointOfContact || '',
            notes: form.notes || '',
            trazodone: form.trazodone || false,
            replaceIvc: form.replaceIvc || false,
            order: form.order ?? (isNew ? nextOrder : undefined)
        };

        setIsModalOpen(false);
        await dbService.saveInpatient(newPatient);
    };

    const filterPatients = (location: HospitalLocation) => {
        return inpatients.filter(p => 
            p.location === location && 
            (p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
             p.diagnosis.toLowerCase().includes(searchTerm.toLowerCase()))
        ).sort((a, b) => {
            const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
            if (orderA === orderB) {
                return a.name.localeCompare(b.name);
            }
            return orderA - orderB;
        });
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = async (event: DragEndEvent, currentList: Inpatient[]) => {
        const { active, over } = event;
        
        if (active.id !== over?.id) {
            const listWithOrder = currentList.map((p, idx) => ({ ...p, order: idx }));
            const oldIndex = listWithOrder.findIndex(p => p.id === active.id);
            const newIndex = listWithOrder.findIndex(p => p.id === over?.id);
            
            if (oldIndex >= 0 && newIndex >= 0) {
                const newList = arrayMove(listWithOrder, oldIndex, newIndex);
                const promises = newList.map((p, idx) => {
                    if (p.order !== idx || currentList.find(orig => orig.id === p.id)?.order !== idx) {
                        return dbService.saveInpatient({ ...p, order: idx });
                    }
                    return Promise.resolve();
                });
                await Promise.all(promises);
            }
        }
    };

    const PatientTable = ({ location, patients, isCollapsed, onToggle }: { location: HospitalLocation, patients: Inpatient[], isCollapsed: boolean, onToggle: () => void }) => (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col">
            <div className={`p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center ${location === 'Mission' ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-teal-50 dark:bg-teal-900/30'}`}>
                <div className="flex items-center gap-2 cursor-pointer select-none" onClick={onToggle}>
                    <button className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400">
                        {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    <div className={`p-2 rounded-lg ${location === 'Mission' ? 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200' : 'bg-teal-200 dark:bg-teal-800 text-teal-800 dark:text-teal-200'}`}>
                        <BedDouble className="w-5 h-5" />
                    </div>
                    <h3 className={`text-lg font-bold brand-font ${location === 'Mission' ? 'text-blue-900 dark:text-blue-300' : 'text-teal-900 dark:text-teal-300'}`}>{location} Inpatients</h3>
                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 shadow-sm">
                        {patients.length}
                    </span>
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={() => handlePrint(location, patients)}
                        className={`p-2 rounded-lg transition-colors flex items-center justify-center ${location === 'Mission' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800' : 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-800'}`}
                        title="Print List"
                    >
                        <Printer className="w-5 h-5" />
                    </button>
                    <button 
                        onClick={() => handleAdd(location)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors ${location === 'Mission' ? 'bg-blue-900 dark:bg-blue-600 text-white hover:bg-blue-800 dark:hover:bg-blue-500' : 'bg-teal-700 dark:bg-teal-600 text-white hover:bg-teal-600 dark:hover:bg-teal-500'}`}
                    >
                        <Plus className="w-4 h-4" /> Add Patient
                    </button>
                </div>
            </div>
            
            {!isCollapsed && (
                <div className="flex-1 overflow-x-auto">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, patients)}>
                    <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 font-bold border-b border-slate-200 dark:border-slate-700">
                        <tr>
                            <th className="p-4 w-10"></th>
                            <th className="p-4 whitespace-nowrap">Patient Name</th>
                            <th className="p-4 whitespace-nowrap">Signalment</th>
                            <th className="p-4 whitespace-nowrap">Diagnosis</th>
                            <th className="p-4 whitespace-nowrap">Procedure</th>
                            <th className="p-4 whitespace-nowrap">POC</th>
                            <th className="p-4 whitespace-nowrap text-center">Trazodone</th>
                            <th className="p-4 whitespace-nowrap text-center">Replace IVC</th>
                            <th className="p-4 min-w-[200px]">Notes</th>
                            <th className="p-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {patients.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="p-8 text-center text-slate-400 dark:text-slate-500 italic">No inpatients listed at {location}.</td>
                            </tr>
                        ) : (
                            <SortableContext items={patients.map(p => p.id)} strategy={verticalListSortingStrategy}>
                                {patients.map(patient => (
                                    <SortablePatientRow
                                        key={patient.id}
                                        patient={patient}
                                        isSearchActive={!!searchTerm}
                                        onEdit={handleEdit}
                                        onDelete={setPatientToDelete}
                                    />
                                ))}
                            </SortableContext>
                        )}
                    </tbody>
                </table>
                </DndContext>
            </div>
            )}
        </div>
    );

    return (
        <div className="h-full flex flex-col p-6 overflow-hidden relative bg-[#0f172a]">
            <div className="mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-3 brand-font">
                        <Stethoscope className="w-8 h-8 text-indigo-400" />
                        Inpatient Census
                    </h2>
                    <p className="text-slate-400 font-medium text-sm">Track active cases across hospitals.</p>
                </div>
                <div className="relative w-full md:w-64">
                    <input
                        type="text"
                        placeholder="Search patients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 rounded-xl border border-white/10 bg-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                    />
                    <Search className="w-4 h-4 text-slate-500 absolute left-3 top-3" />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto pb-4">
                <div className="flex flex-col gap-8">
                    <div>
                        <PatientTable 
                            location="Mission" 
                            patients={filterPatients('Mission')} 
                            isCollapsed={!!collapsedLocations['Mission']}
                            onToggle={() => toggleCollapse('Mission')}
                        />
                    </div>
                    <div>
                        <PatientTable 
                            location="OP" 
                            patients={filterPatients('OP')} 
                            isCollapsed={!!collapsedLocations['OP']}
                            onToggle={() => toggleCollapse('OP')}
                        />
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
                            <h3 className="font-bold text-slate-800 dark:text-white brand-font flex items-center gap-2">
                                {editingId ? <Edit2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                {editingId ? 'Edit Patient' : 'Add New Patient'} - <span className="text-blue-600 dark:text-blue-400">{form.location}</span>
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        
                        <div className="p-6 space-y-4 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Patient Name</label>
                                    <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                        value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Name" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Signalment</label>
                                    <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                        value={form.signalment} onChange={e => setForm({...form, signalment: e.target.value})} placeholder="e.g. 5y FS Lab" />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Diagnosis</label>
                                    <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                        value={form.diagnosis} onChange={e => setForm({...form, diagnosis: e.target.value})} placeholder="Diagnosis" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Procedure</label>
                                    <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                        value={form.procedure} onChange={e => setForm({...form, procedure: e.target.value})} placeholder="Procedure" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Point of Contact</label>
                                <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                    value={form.pointOfContact} onChange={e => setForm({...form, pointOfContact: e.target.value})} placeholder="Doctor or Tech Name" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={form.trazodone || false} 
                                        onChange={(e) => setForm({...form, trazodone: e.target.checked})}
                                        className="w-4 h-4 text-indigo-600 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:ring-indigo-500 cursor-pointer"
                                    />
                                    Trazodone
                                </label>
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={form.replaceIvc || false} 
                                        onChange={(e) => setForm({...form, replaceIvc: e.target.checked})}
                                        className="w-4 h-4 text-indigo-600 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:ring-indigo-500 cursor-pointer"
                                    />
                                    Replace IVC
                                </label>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Additional Notes</label>
                                <textarea className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none h-64 resize-y text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                    value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Status updates, plan, etc." />
                            </div>
                        </div>

                        <div className="p-4 bg-slate-50 dark:bg-slate-900 flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700">
                            <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-600 dark:text-slate-400 font-medium hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
                            <button onClick={handleSave} className="px-4 py-2 bg-blue-900 dark:bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-800 dark:hover:bg-blue-500 flex items-center gap-2">
                                <Save className="w-4 h-4" /> Save Patient
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {patientToDelete && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-sm w-full p-6 text-center animate-in zoom-in-95 duration-200">
                        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-red-50 dark:border-red-900/50">
                            <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 brand-font">Delete Patient?</h3>
                        <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed">
                            Are you sure you want to remove <span className="font-bold text-slate-800 dark:text-white">{patientToDelete.name}</span> from the inpatient list? This action cannot be undone.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button 
                                onClick={() => setPatientToDelete(null)}
                                className="px-4 py-2.5 text-slate-600 dark:text-slate-400 font-bold hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors w-full"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmDelete}
                                className="px-4 py-2.5 bg-red-600 dark:bg-red-500 text-white font-bold rounded-xl hover:bg-red-700 dark:hover:bg-red-600 transition-colors shadow-lg shadow-red-900/20 w-full"
                            >
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
